import React, { useEffect, useState } from "react";
import Breadcrumb from "../../../components/common/Breadcrumbs";
import { SearchInputWrapper } from "../../../components/common/datatable/filter/SearchInputWrapper";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { useSearchIncidentsQuery, useLazyExportIncidentsQuery } from "./incidentsApi";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import { formatIsoDateString} from "../../../helpers/utils/DateUtils";
import { SearchDatePickerWrapper } from "../../../components/common/datatable/filter/SearchDatePickerWrapper";
import { useGetAllTenantsQuery } from "../../customer/tenantApi";
import { createClickableLink } from "../../../components/common/datatable/DataTableUtils";
import {hasPermission, jwtDecode} from "../../../helpers/utils/AccessControlUtils";
import ModalButton from "../../../components/common/form/ModalButton";
import IconButton from "../../../components/common/form/IconButton";
import { FaEdit } from "react-icons/fa";
import IncidentModal from "../../order/modal/AddOrEditIncident";
import { useLazyGetAllCustomerByTenantQuery} from "../../customer/customerApi";
import {SearchSelectWithList} from "../../../components/common/datatable/filter/SearchSelectWrapper";
import {useLazyGetAllUserListByCustomerIdQuery} from "../../user/users/usersApi";

const Incidents = () => {
    const [triggerExportIncidentsDataQuery] = useLazyExportIncidentsQuery();
    const [getAllCustomerTenantApi, getAllCustomerTenantApiResponse] = useLazyGetAllCustomerByTenantQuery();
    const [usersByCustomerApi, usersByCustomerApiResponse] = useLazyGetAllUserListByCustomerIdQuery();
    const tenantDataApi = useGetAllTenantsQuery("");
    const customerLevel = jwtDecode()?.customerLevel;


    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "Incidents", url: "/incidents" },
    ]);

    useEffect(() => {
        document.title = "Incidents";
    });

    useEffect(() => {
        if (customerLevel !== 'SYS') {
            getAllCustomerTenantApi({ tenantName: jwtDecode()?.tenant });
        }
    }, [customerLevel]);


    const columns: DataTableColumn[] = [
        {
            label: "Order Number",
            accessor: "customerOrderNumber",
            canSort: false,
            cell: (props: any) => {
                let path = "/orders/" + props.row.original.orderId;
                return createClickableLink(path, props.value, "");
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper
                    accessor={'customerOrderNumber'}
                    placeholder={'Enter Order Number...'}
                    name={'orderNumber'} />
            }]
        },

        {
            label: "Tenant",
            accessor: "tenant",
            visibility: {canColumnShow: false },
            filterOptions: [{
                dependentColumns: ["Reporting Party", "Responsible User"],
                filterComponent: SearchSelectWithList(tenantDataApi?.data, "name", "name", "Select Tenant...", false, "tenant"),
            }],
        },

        {
            label: "Reporting Party",
            accessor: "reportingParty",
            filterOptions: [{
                dependentColumns: ["Reporting User"],
                filterComponent: SearchSelectWithList(getAllCustomerTenantApiResponse?.data, (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Reporting Party...", false, "reportingParty"),
                onDependencyUpdated: (value: any) => {
                    getAllCustomerTenantApi({ tenantName: value });
                },
            }],
        },

        {
            label: "Reporting User",
            accessor: "reportingUser",
            visibility: { hasShowSetting: false },
            canSort: false,
            filterOptions: [{
                filterComponent: SearchSelectWithList(usersByCustomerApiResponse?.data, "username", "id", "Select Reporting User...", false, "reportingUser"),
                onDependencyUpdated: (value: any) => {
                    usersByCustomerApi(value)
                },
            }]
        },

        {
            label: "Responsible Party",
            accessor: "responsibleParty",
            canSort: false,
            filterOptions: [{
                dependentColumns: ["Responsible User"],
                filterComponent: SearchSelectWithList(getAllCustomerTenantApiResponse?.data, (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Responsible Party...", false, "responsibleParty"),
                onDependencyUpdated: (value: any) => {
                    getAllCustomerTenantApi({ tenantName: value });
                },
            }]

        },

        {
            label: "Responsible User",
            accessor: "responsibleUser",
            visibility: { hasShowSetting: false },
            canSort: false,
            filterOptions: [{
                filterComponent: SearchSelectWithList(usersByCustomerApiResponse?.data, "name", "id", "Select Responsible User...", false, "reportingUser"),
                onDependencyUpdated: (value: any) => {
                    usersByCustomerApi(value)
                },
            }]
        },

        {
            label: "Reported Date",
            accessor: "reportedDate",
            cell: (props: any) => {
                return formatIsoDateString(props.value);
            }, filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"reportedDateFrom"} />,
                overrideFilterAccessor: "reportedDateFrom",
                overrideFilterLabel: "Reported Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"reportedDateTo"} />,
                overrideFilterAccessor: "reportedDateTo",
                overrideFilterLabel: "Reported Date - To",
            }]
        },
        {
            label: "Resolution Date", accessor: "resolutionDate", cell: (props: any) => {
                return formatIsoDateString(props.value);
            }, filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"resolutionDateFrom"} />,
                overrideFilterAccessor: "resolutionDateFrom",
                overrideFilterLabel: "Resolution Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"resolutionDateTo"} />,
                overrideFilterAccessor: "resolutionDateTo",
                overrideFilterLabel: "Resolution Date - To",
            }]
        },
        {
            label: "Dev Ticket Reference",
            accessor: "devTicketReference",
            canSort: false,
            filterOptions: [{
                filterComponent: <SearchInputWrapper
                    accessor={'devTicketReference'}
                    placeholder={'Enter Dev Ticket Reference...'}
                    name={'devTicketReference'} />
            }]
        },
        {
            label: "Action", visibility: {
                canColumnShow: hasPermission(["TEAM-U"]),
                showByDefault: hasPermission(["TEAM-U"]),
                hasShowSetting: false,
            },
            accessor: "creationDate",
            canSort: false,
            cell: (props: any) => {
                return <>

                    <div className="ml-4">
                        {hasPermission(["TEAM-U"]) && (


                            <ModalButton
                                triggerButton={
                                    <IconButton
                                        icon={<FaEdit />}
                                        title={"Delete Customer Pricing"}
                                        hasBackground={false}
                                        className="mr-2 text-highlight-0"
                                    />}

                                modal={
                                    <IncidentModal show={true} setShow={() => {
                                    }} title={"Update Incident"} incidentId={ props.row.original.id} btnText={"Update"} orderId={ props.row.original.orderId}
                                    />
                                }
                            />

                        )}
                    </div>
                </>
            }
        }

    ]



    return (
        <div className="user-container px-5 py-5">
            <div className="flex justify-between">
                <Breadcrumb crumbs={crumbs} />
            </div>

            <DataTable
                columns={columns}
                actions={{
                    fetchAction: useSearchIncidentsQuery,
                    exportAction: triggerExportIncidentsDataQuery
                }}

                basePermission={"INCR"}
                userSettingGroup={"Incidents"}
            />
        </div>
    )
}
export default Incidents;